import { Button } from '@ulam/ube'
import { ArrowUp, PinOff } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { announce } from '@ulam/taho'
import { useT } from '../hooks/useTranslate.js'
import { useKeydown, useFlipList, usePrefersReducedMotion } from '@ulam/sili/react'

import A11yResultAd from './A11yResultAd.jsx'
import AppListResultCard from './AppListResultCard.jsx'
import AppResultsMetaHeader from './AppResultsMetaHeader.jsx'
import A11yResultsActiveFilterBar from './A11yResultsActiveFilterBar.jsx'
import Screen from './A11yScreen.jsx'
import { DEFAULT_RATING, pluralResult, ARCHIVE_FOCUS_DELAY_MS, RESULTS_VIEW_ALL_THRESHOLD, UNPIN_FLY_MS } from '../utils/constants.js'
import useSwipeReveal from '../hooks/useSwipeReveal.js'
import useAnimationStates from '../hooks/useAnimationStates.js'
import { useSettings } from '../context/contextSettings.js'
import { useSearch } from '../context/contextSearch.js'
import { useRatings } from '../context/contextRatings.js'
import './app-screen-results.css'

function getDisplayCountLabel(narrowMode, narrowResults, filterLabel, hasPinnedItems, results, t) {
  if (narrowMode && narrowResults) {
    return t('results.narrow_count', { narrowed: narrowResults.length, total: results.length, result: pluralResult(narrowResults.length) })
  }
  if (filterLabel) {
    return t('results.count_badge', { count: results.length, result: pluralResult(results.length), filter: filterLabel })
  }
  if (hasPinnedItems) {
    return t('results.count_more', { count: results.length, result: pluralResult(results.length) })
  }
  return t('results.count', { count: results.length, result: pluralResult(results.length) })
}

export function PinnedSection({ entries, onSelect, onClearPins, headingRef }) {
  const t = useT()
  const { showVoting: showRanking } = useSettings()
  const prefersReducedMotion = usePrefersReducedMotion()
  const [clearingAll, setClearingAll] = useState(false)
  if (!entries.length) return null

  const handleClearAll = () => {
    if (prefersReducedMotion || !onClearPins) { onClearPins?.(); return }
    setClearingAll(true)
    setTimeout(() => { onClearPins(); setClearingAll(false) }, UNPIN_FLY_MS + 80)
  }

  const pinnedSectionClass = `pinned-section pinned-results${clearingAll ? ' pinned-section--clearing' : ''}`

  return (
    <div className={pinnedSectionClass}>
      <div className="pinned-section__header">
        <h2 ref={headingRef} tabIndex={-1} className="pinned-section__heading">
          {t('results.pinned_heading')}
          <span className="pinned-section__count">{entries.length}</span>
        </h2>
        {onClearPins && (
          <Button variant="tertiary" className="pinned-unpin-all-btn" onClick={handleClearAll} icon={<PinOff size={14} aria-hidden="true" />}>
            {t('results.unpin_all')}
          </Button>
        )}
      </div>
      <AppScreenResults
        results={entries}
        onSelect={onSelect}
        selected={null}
        query=""
        showRankingSort={showRanking}
        hideCount
        hideFilters
        hasPinnedItems={false}
      />
    </div>
  )
}

export default function AppScreenResults({ results, selected, onSelect, query, countRef, onCopyLink, hideCount = false, hideFilters = false, filterLabel, narrowResults = null, _showRankingSort = false, showAds = false, adFrequency = 8, onClear, onClearQuery, hasPinnedItems = false, defaultWcagFilter = null, onOpenSettings, onBadgeClick, isBadgeFiltered = false }) {
  const { liveSearch, showVoting: showRanking, platform, setPlatform: onPlatformChange, wcagFilter, setWcagFilter } = useSettings()
  const { narrowMode, narrowQuery, sortBy, setSortBy: onSortChange, setNarrowMode, setNarrowQuery, setSubmittedNarrowQuery } = useSearch()
  const onNarrow = () => setNarrowMode(true)
  const onNarrowExit = useCallback(() => { setNarrowMode(false); setNarrowQuery(''); setSubmittedNarrowQuery('') }, [setNarrowMode, setNarrowQuery, setSubmittedNarrowQuery])
  const onNarrowChange = setNarrowQuery
  const onNarrowSearch = () => setSubmittedNarrowQuery(narrowQuery)
  const { ratings, rankUp: onRankUp, rankDown: onRankDown, toggleStar: onStar, toggleArchive: onArchive, pinnedIds, togglePin: onPin } = useRatings()
  const t = useT()
  const platformLabels = useMemo(() => ({
    all: t('settings.platform_all'),
    web: t('settings.platform_web'),
    native: t('settings.platform_native'),
    document: t('settings.platform_document'),
  }), [t])
  const itemRefs = useRef({})
  const defaultCountHeadingRef = useRef(null)
  const countHeadingRef = countRef ?? defaultCountHeadingRef
  const [linkCopied, setLinkCopied] = useState(false)
  const [pendingSort, setPendingSort] = useState(sortBy)
  const [sortToCommit, setSortToCommit] = useState(null)
  const [clearPending, setClearPending] = useState(false)
  const [sortFlash, setSortFlash] = useState(false)
  const { listRef, snapshotPositions } = useFlipList()
  const {
    animatingUp, setAnimatingUp,
    animatingDown, setAnimatingDown,
    pinningIds, setPinningIds,
    unpinningIds, setUnpinningIds,
    archivingIds, setArchivingIds,
    unarchivingIds, setUnarchivingIds,
  } = useAnimationStates()
  const prevNarrowResultsRef = useRef(undefined)
  const { swipeOpenId, setSwipeOpenId, swipeTouchRef } = useSwipeReveal({ listRef, showRanking, onPin, pinnedIds })

  useEffect(() => { setPendingSort(sortBy) }, [sortBy]) // eslint-disable-line react-hooks/set-state-in-effect -- intentional sync from parent prop

  useEffect(() => {
    if (sortToCommit === null) return
    onSortChange(sortToCommit)
    setSortToCommit(null) // eslint-disable-line react-hooks/set-state-in-effect
  }, [sortToCommit]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!clearPending) return
    setClearPending(false) // eslint-disable-line react-hooks/set-state-in-effect
    onClear?.()
  }, [clearPending]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const prev = prevNarrowResultsRef.current
    prevNarrowResultsRef.current = narrowResults
    if (!narrowMode || narrowResults === null || narrowResults === undefined) return
    if (prev === undefined) return
    const count = narrowResults.length
    const result = pluralResult(count)
    const platformLabel = platformLabels[platform] ?? platform
    if (platform !== 'all') {
      announce(t('announce.narrow_results_platform', { count, result, total: results.length, platform: platformLabel }))
    } else {
      announce(t('announce.narrow_results', { count, result, total: results.length }))
    }
  }, [narrowResults]) // eslint-disable-line react-hooks/exhaustive-deps

  const displayResults = narrowMode && narrowResults ? narrowResults : results
  const displayCount = getDisplayCountLabel(narrowMode, narrowResults, filterLabel, hasPinnedItems, results, t)
  const [visibleCount, setVisibleCount] = useState(50)
  const [prevDisplayResults, setPrevDisplayResults] = useState(displayResults)
  const observerRef = useRef(null)

  if (displayResults !== prevDisplayResults) {
    setPrevDisplayResults(displayResults)
    setVisibleCount(50)
  }

  useEffect(() => {
    if (!observerRef.current) return
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setVisibleCount(prev => Math.min(prev + 50, displayResults.length))
      }
    }, { rootMargin: '400px' })
    observer.observe(observerRef.current)
    return () => observer.disconnect()
  }, [displayResults.length])

  const handleKeyDown = useCallback((e) => {
    if (!listRef.current) return
    const focusedBtn = document.activeElement?.closest('.result-item')
    if (!focusedBtn) return

    const currentIndex = displayResults.findIndex(r => itemRefs.current[r.id] === document.activeElement)
    if (currentIndex === -1) return

    const currentEntry = displayResults[currentIndex]
    const rating = ratings[currentEntry.id] || DEFAULT_RATING
    const { starred, archived } = rating
    const searchInput = document.querySelector('input[type="search"]')
    const isTyping = searchInput === document.activeElement

    if (isTyping) return

    switch (e.key.toLowerCase()) {
      case 'j':
        e.preventDefault()
        if (currentIndex < displayResults.length - 1) {
          itemRefs.current[displayResults[currentIndex + 1].id]?.focus()
        }
        break
      case 'k':
        e.preventDefault()
        if (currentIndex > 0) {
          itemRefs.current[displayResults[currentIndex - 1].id]?.focus()
        }
        break
      case 's':
        e.preventDefault()
        onStar?.(currentEntry.id)
        announce(starred ? t('announce.unstarred') : t('announce.starred'))
        break
      case 'e':
        e.preventDefault()
        announce(archived ? t('announce.unarchived') : t('announce.archived'), { priority: 'assertive' })
        setTimeout(() => onArchive?.(currentEntry.id), ARCHIVE_FOCUS_DELAY_MS)
        break
      case 'u':
        e.preventDefault()
        if (archived) {
          onArchive?.(currentEntry.id)
          announce(t('results.unarchived_aria_live'))
        }
        break
      default:
        if (e.shiftKey && (e.key === 'ArrowUp' || e.key === '↑')) {
          e.preventDefault()
          onRankUp?.(currentEntry.id)
          const newRating = ratings[currentEntry.id] || DEFAULT_RATING
          announce(t('announce.ranked_up', { title: currentEntry.title, score: newRating.score + 1 }))
        } else if (e.shiftKey && (e.key === 'ArrowDown' || e.key === '↓')) {
          e.preventDefault()
          onRankDown?.(currentEntry.id)
          const newRating = ratings[currentEntry.id] || DEFAULT_RATING
          announce(t('announce.ranked_down', { title: currentEntry.title, score: newRating.score - 1 }))
        }
    }
  }, [displayResults, ratings, onStar, onArchive, onRankUp, onRankDown, t]) // eslint-disable-line react-hooks/exhaustive-deps -- listRef is a stable ref

  useKeydown(handleKeyDown, { target: listRef })

  useEffect(() => {
    if (!swipeOpenId) return
    function handleOutsideTap(e) {
      if (!e.target.closest(`[data-swipe-id="${swipeOpenId.id}"]`)) {
        setSwipeOpenId(null)
      }
    }
    document.addEventListener('pointerdown', handleOutsideTap)
    return () => document.removeEventListener('pointerdown', handleOutsideTap)
  }, [swipeOpenId]) // eslint-disable-line react-hooks/exhaustive-deps -- setSwipeOpenId is stable

  const isQueryFilterActive = query && onClearQuery
  const isNarrowFilterActive = narrowMode && narrowQuery
  const isPlatformFilterActive = onPlatformChange && platform !== 'all'
  const isBadgeFilterActive = isBadgeFiltered && filterLabel && onClear
  const isWcagVersionFilterActive = !filterLabel && !isBadgeFiltered && wcagFilter && defaultWcagFilter && wcagFilter.maxVersion !== defaultWcagFilter.maxVersion
  const isWcagLevelFilterActive = !filterLabel && !isBadgeFiltered && wcagFilter && wcagFilter.maxLevel !== 'AAA'

  const activeFilters = useMemo(() => [
    isQueryFilterActive ? { label: `"${query}"`, onRemove: onClearQuery } : null,
    isNarrowFilterActive ? { label: t('results.filter_narrow', { query: narrowQuery }), onRemove: onNarrowExit } : null,
    isPlatformFilterActive ? { label: platformLabels[platform] ?? platform, onRemove: () => onPlatformChange('all') } : null,
    isBadgeFilterActive ? { label: filterLabel, onRemove: onClear } : null,
    isWcagVersionFilterActive ? { label: `WCAG ${wcagFilter?.maxVersion}`, onRemove: () => setWcagFilter({ ...wcagFilter, maxVersion: defaultWcagFilter.maxVersion }) } : null,
    isWcagLevelFilterActive ? { label: `Level ${wcagFilter?.maxLevel}`, onRemove: () => setWcagFilter({ ...wcagFilter, maxLevel: 'AAA' }) } : null,
  ].filter(Boolean), [isQueryFilterActive, query, onClearQuery, isNarrowFilterActive, narrowQuery, onNarrowExit, isPlatformFilterActive, platform, platformLabels, onPlatformChange, isBadgeFilterActive, filterLabel, onClear, isWcagVersionFilterActive, wcagFilter, defaultWcagFilter, setWcagFilter, isWcagLevelFilterActive, t])

  const hasNonDefaultSort = sortBy !== 'smart'
  const isWcagFilterActiveForClear = (wcagFilter?.maxVersion !== defaultWcagFilter?.maxVersion) || (wcagFilter?.maxLevel !== 'AAA')
  const hasAnyActiveFilter = isQueryFilterActive || isNarrowFilterActive || isPlatformFilterActive || isBadgeFilterActive || isWcagFilterActiveForClear || hasNonDefaultSort

  if (results.length === 0 && platform === 'all') {
    return <Screen
      variant="no-results"
      ariaLabel={t('results.no_results_aria_live')}
      heading={t('results.no_results_heading', { query })}
      body={t('results.no_results_body')}
      onMount={() => announce(t('results.no_results_announce', { query }))}
      activeFilters={activeFilters}
      action={hasAnyActiveFilter ? onClear : undefined}
      actionLabel={hasAnyActiveFilter ? t('results.no_results_clear_filters') : undefined}
      onOpenSettings={onOpenSettings}
    />
  }

  const platformNoResults = results.length === 0 && platform !== 'all'
  const narrowNoResults = narrowMode && narrowResults && narrowResults.length === 0

  const sortLabels = {
    smart: t('results.sort_smart'),
    relevance: t('results.sort_relevance'),
    'severity-desc': t('results.sort_severity_desc'),
    'severity-asc': t('results.sort_severity_asc'),
    'title-az': t('results.sort_title_az'),
    'title-za': t('results.sort_title_za'),
    sc: t('results.sort_sc'),
    'wcag-version': t('results.sort_wcag_version'),
    'wcag-level': t('results.sort_wcag_level'),
    platform: t('results.sort_platform'),
    popularity: t('results.sort_popularity'),
  }

  return (
    <div className="result-list-section">
      <AppResultsMetaHeader
        displayCount={displayCount}
        query={query}
        onCopyLink={onCopyLink}
        linkCopied={linkCopied}
        setLinkCopied={setLinkCopied}
        sortBy={sortBy}
        pendingSort={pendingSort}
        setPendingSort={setPendingSort}
        onSortChange={onSortChange}
        setSortToCommit={setSortToCommit}
        setSortFlash={setSortFlash}
        sortFlash={sortFlash}
        showRankingSort={showRanking}
        narrowMode={narrowMode}
        narrowResults={narrowResults}
        narrowQuery={narrowQuery}
        onNarrowChange={onNarrowChange}
        onNarrowSearch={onNarrowSearch}
        liveSearch={liveSearch}
        onOpenSettings={onOpenSettings}
        onNarrow={onNarrow}
        onNarrowExit={onNarrowExit}
        platform={platform}
        onPlatformChange={onPlatformChange}
        hideCount={hideCount}
        hideFilters={hideFilters}
        results={results}
        platformNoResults={platformNoResults}
        platformLabels={platformLabels}
        onClear={() => { announce(t('results.filters_cleared_aria_live')); setClearPending(true); onClear?.() }}
        countHeadingRef={countHeadingRef}
        hasAnyActiveFilter={hasAnyActiveFilter}
      />

      {!hideFilters && results.length > 0 && !platformNoResults && !narrowNoResults && (
        <>
          <A11yResultsActiveFilterBar
            activeFilters={activeFilters}
            sortBy={sortBy}
            sortLabels={sortLabels}
            onSortChange={onSortChange}
            hasNonDefaultSort={hasNonDefaultSort}
          />
          {onPlatformChange && narrowMode}
        </>
      )}

      {(narrowNoResults || platformNoResults)
        ? <Screen
          variant="no-results"
          ariaLabel={t('results.no_results_aria_live')}
          heading={t('results.no_results_heading', { query: narrowNoResults ? narrowQuery : query })}
          body={t('results.no_results_body')}
          activeFilters={activeFilters}
          action={narrowNoResults || hasAnyActiveFilter ? onClear : undefined}
          actionLabel={narrowNoResults || hasAnyActiveFilter ? t('results.no_results_clear_filters') : undefined}
          onOpenSettings={onOpenSettings}
        />
        : <ul ref={listRef} className={`result-list${selected ? ' result-list--has-selection' : ''}${hasPinnedItems ? ' result-list--has-pinned' : ''}`} aria-label={t('results.aria_label')}>
          {displayResults.slice(0, visibleCount).map((entry, index) => {
            const showAdAfter = showAds && adFrequency > 0 && (index + 1) % adFrequency === 0
            return (
              <React.Fragment key={entry.id}>
                <AppListResultCard
                  key={entry.id}
                  entry={entry}
                  index={index}
                  selected={selected}
                  onSelect={onSelect}
                  displayResults={displayResults}
                  showRanking={showRanking}
                  showRankingSort={showRanking}
                  onPin={onPin}
                  onRankUp={onRankUp}
                  onRankDown={onRankDown}
                  onStar={onStar}
                  onArchive={onArchive}
                  onBadgeClick={onBadgeClick}
                  ratings={ratings}
                  pinnedIds={pinnedIds}
                  animatingUp={animatingUp}
                  animatingDown={animatingDown}
                  archivingIds={archivingIds}
                  unarchivingIds={unarchivingIds}
                  pinningIds={pinningIds}
                  unpinningIds={unpinningIds}
                  setAnimatingUp={setAnimatingUp}
                  setAnimatingDown={setAnimatingDown}
                  setArchivingIds={setArchivingIds}
                  setUnarchivingIds={setUnarchivingIds}
                  setPinningIds={setPinningIds}
                  setUnpinningIds={setUnpinningIds}
                  swipeOpenId={swipeOpenId}
                  setSwipeOpenId={setSwipeOpenId}
                  swipeTouchRef={swipeTouchRef}
                  itemRefs={itemRefs}
                  snapshotPositions={snapshotPositions}
                />
                {showAdAfter && <A11yResultAd key={`ad-${entry.id}`} />}
              </React.Fragment>
            )
          })}
          {displayResults.length > visibleCount && (
            <li ref={observerRef} style={{ height: '1px', visibility: 'hidden' }} aria-hidden="true" />
          )}
        </ul>
      }
      {results.length > 0 && !hideCount && !narrowNoResults && !platformNoResults && visibleCount >= results.length && (
        <p className="results-end-marker">{t('results.end_of_results')}</p>
      )}
      {results.length > RESULTS_VIEW_ALL_THRESHOLD && (
        <div className="view-all-section">
          <Button
            variant="secondary"
            className="btn-back-to-top"
            onClick={() => {
              const drawer = document.querySelector('.drawer-panel.is-open')
                ; (drawer ?? window).scrollTo({ top: 0, behavior: 'smooth' })
              countHeadingRef.current?.focus({ preventScroll: true })
            }}
          >
            <ArrowUp size={16} aria-hidden="true" />
            {t('results.back_to_top')}
          </Button>
        </div>
      )}
    </div>
  )
}

export { default as A11yResultSkeleton } from './A11yResultSkeleton.jsx'
